/**
 * The lean vehicle table inside one worklist group: Vehicle / VIN / Stock /
 * Rooftop / Age / Photos / worded vendor-status matrix. The problem is named once in
 * the group header, so rows carry data, not chips. Header/zebra styling
 * matches components/audit/AuditTable.tsx (headerCellStyle ~205, dataCellStyle
 * ~238); the column set is the worklist's own, per the approved mockup.
 */
import React from "react"

import { VehicleResponse } from "../../../api/audit"
import { THEME } from "../../../config/theme"
import { formatDaysOpen, formatProblemDetail, isReversibleDeparture } from "../../../utils/formatAge"
import { parseUtcTimestamp } from "../../../utils/parseUtcTimestamp"
import VendorMatrix from "../VendorMatrix"

interface Props {
  vehicles: VehicleResponse[]
  /** The group's severity. A critical group's rows show days open in the tag
   *  column instead of NEW (revenue-assurance plan). */
  severity?: string
  /** VINs first flagged with this problem in this run (run-over-run diff). */
  newVins?: Set<string>
  /** vin → marked_at ISO date for rows the operator marked worked. */
  workedMarks?: Map<string, string>
  onRowClick?: (vehicle: VehicleResponse) => void
  /**
   * When rows span problem types (worklist search results), names each row's
   * problem in a leading column — inside a group the header names it once.
   */
  problemLabelFor?: (vehicle: VehicleResponse) => string | null
  /** The column the server is ordering by, or null for the group's default. */
  sort?: GroupSort | null
  /**
   * Click handler for a sortable header. ABSENT means this table cannot be
   * sorted at all, and the headers render as plain labels — search results
   * (which span groups) and Dealer Duplicates both pass nothing.
   */
  onSort?: (field: string) => void
}

export interface GroupSort {
  field: string
  direction: "asc" | "desc"
}

/**
 * Each header and the stored column the server orders it by. `field: null` is
 * a column with nothing orderable behind it: SITE is a link, and the tag column
 * holds NEW / worked badges that come from the run-over-run diff and the
 * operator's own marks rather than from the car's record.
 *
 * VEHICLE sorts on year — the cell's leading, orderable part. VENDOR STATUS
 * stacks five feeds in one cell and sorts on Tekion, the system of record.
 * Names must match `queries/audit.py::GROUP_SORTABLE_FIELDS`.
 */
const BASE_HEADERS: Array<{ label: string; field: string | null }> = [
  { label: "VEHICLE", field: "vehicle" },
  { label: "VIN", field: "vin" },
  { label: "STOCK", field: "stock" },
  { label: "ROOFTOP", field: "rooftop" },
  { label: "AGE", field: "age_days" },
  // HomeNet's photo count (revenue-assurance plan, Phase 1b). Zero is the
  // fact that sends a sale-ready, not-on-site car to the photographer.
  { label: "PHOTOS", field: "photos_count" },
  { label: "VENDOR STATUS", field: "vendor_status" },
]

const headerCellStyle: React.CSSProperties = {
  background: THEME.auditTable.headerBackground,
  color: THEME.colors.white,
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textAlign: "left",
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  whiteSpace: "nowrap",
}

const dataCellStyle = (background: string): React.CSSProperties => ({
  fontSize: 12,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
  whiteSpace: "nowrap",
  background,
})

function vehicleName(vehicle: VehicleResponse): string {
  return [vehicle.year, vehicle.make, vehicle.model]
    .filter((part) => part !== null && part !== undefined && `${part}`.trim() !== "")
    .join(" ") || "—"
}

/**
 * Group rows into VIN families, preserving fetch order (for duplicate-vin the
 * backend serves the sheet's own order, so a family's counterpart records —
 * including "Not shown" zombie twins — arrive adjacent to their parent).
 * Families of one render as plain rows.
 */
function groupIntoFamilies(vehicles: VehicleResponse[]): VehicleResponse[][] {
  const families: VehicleResponse[][] = []
  const byVin = new Map<string, VehicleResponse[]>()
  for (const vehicle of vehicles) {
    const vin = (vehicle.vin ?? "").trim()
    const existing = vin ? byVin.get(vin) : undefined
    if (existing) {
      existing.push(vehicle)
    } else {
      const family = [vehicle]
      families.push(family)
      if (vin) byVin.set(vin, family)
    }
  }
  return families
}

export default function GroupVehicleTable({
  vehicles, severity, newVins, workedMarks, onRowClick, problemLabelFor, sort, onSort,
}: Props) {
  // Duplicate-VIN rows carry their subtype ("Same VIN — New + Used") as
  // problem_detail; the column appears only when a row actually has one.
  const showDetail = vehicles.some((vehicle) => vehicle.problem_detail)
  // Quick link to the live listing, right in the row (Ian) — only when at
  // least one row in the group actually has a listing to open.
  const showSite = vehicles.some((vehicle) => vehicle.website_url)
  // What the SITE says about the car (ghost group etc.) — worth a column
  // only when at least one row carries a label.
  const showSiteLabel = vehicles.some((vehicle) => vehicle.site_label)
  // A critical row carries how long it has been open instead of NEW — a
  // critical is past its grace by definition, so "just appeared" must never
  // read as "nothing to fix" (revenue-assurance plan).
  const showDaysOpen = severity === "critical" && vehicles.some((vehicle) => vehicle.first_seen_at)
  const showTags = Boolean(newVins?.size || workedMarks?.size || showDaysOpen)
  const headers: Array<{ label: string; field: string | null }> = [
    ...(problemLabelFor ? [{ label: "PROBLEM", field: null }] : []),
    ...BASE_HEADERS,
    ...(showDetail ? [{ label: "DETAIL", field: "detail" }] : []),
    ...(showSiteLabel ? [{ label: "SITE SAYS", field: "site_label" }] : []),
    ...(showSite ? [{ label: "SITE", field: null }] : []),
    ...(showTags ? [{ label: showDaysOpen ? "OPEN" : "", field: null }] : []),
  ]

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            {headers.map(({ label, field }) => {
              const sortable = Boolean(onSort && field)
              const active = sortable && sort?.field === field
              return (
                <th
                  key={label}
                  style={{ ...headerCellStyle, cursor: sortable ? "pointer" : "default" }}
                  onClick={sortable ? () => onSort!(field!) : undefined}
                  title={sortable ? "Click to sort" : undefined}
                  aria-sort={
                    active ? (sort!.direction === "asc" ? "ascending" : "descending") : undefined
                  }
                >
                  {label}
                  {/* U+25B2 / U+25BC — the same arrows the sheet view uses, and
                      neither is an emoji codepoint, so iOS renders them as
                      glyphs rather than substituting Apple Color Emoji. */}
                  {active && (
                    <span style={{ marginLeft: 4, fontSize: 9 }}>
                      {sort!.direction === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {groupIntoFamilies(vehicles).flatMap((family, familyIndex) => {
            // Zebra alternates per VIN family so counterpart rows read as one block.
            const zebra =
              familyIndex % 2 === 0 ? THEME.auditTable.zebraLight : THEME.auditTable.zebraMain
            return family.map((vehicle, memberIndex) => {
            const isCounterpart = memberIndex > 0
            const vin = vehicle.vin ?? ""
            const isNew = newVins?.has(vin) ?? false
            const workedAt = workedMarks?.get(vin)
            return (
              <tr
                key={vehicle.id}
                onClick={onRowClick ? () => onRowClick(vehicle) : undefined}
                title={onRowClick ? "Click for details and actions" : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {problemLabelFor && (
                  <td style={{ ...dataCellStyle(zebra), fontWeight: 500 }}>
                    {problemLabelFor(vehicle) ?? "—"}
                  </td>
                )}
                <td
                  style={{
                    ...dataCellStyle(zebra),
                    fontWeight: isCounterpart ? 400 : 500,
                    ...(isCounterpart
                      ? { paddingLeft: `calc(${THEME.spacing.sm} + 18px)`, color: THEME.colors.neutral.light.textSecondary }
                      : {}),
                  }}
                >
                  {isCounterpart ? "└ " : ""}
                  {vehicleName(vehicle)}
                </td>
                <td style={{ ...dataCellStyle(zebra), fontFamily: THEME.fonts.mono, fontSize: 11.5, color: THEME.colors.neutral.light.textSecondary }}>
                  {vehicle.vin ?? "—"}
                </td>
                <td style={dataCellStyle(zebra)}>{vehicle.stock ?? "—"}</td>
                {/* Duplicates rows store their rooftop as tekion_rooftop (the
                    sheet's own column — rooftops can differ per record). */}
                <td style={dataCellStyle(zebra)}>{vehicle.rooftop ?? vehicle.tekion_rooftop ?? "—"}</td>
                <td style={{ ...dataCellStyle(zebra), fontVariantNumeric: "tabular-nums" }}>
                  {vehicle.age_days ?? "—"}
                </td>
                <td
                  style={{
                    ...dataCellStyle(zebra),
                    fontVariantNumeric: "tabular-nums",
                    // Zero photos is the finding, not a quiet number.
                    ...(vehicle.photos_count === 0
                      ? { color: THEME.colors.semantic.danger, fontWeight: 600 }
                      : {}),
                  }}
                >
                  {vehicle.photos_count ?? "—"}
                </td>
                <td style={dataCellStyle(zebra)}>
                  <VendorMatrix
                    tkBucket={vehicle.tk_bucket}
                    rrBucket={vehicle.rr_bucket}
                    hnBucket={vehicle.hn_bucket}
                    millBucket={vehicle.mill_bucket}
                    tkRaw={vehicle.tekion_status}
                    rrRaw={vehicle.rr_step}
                    millRaw={vehicle.rr_recon_step}
                    hnRaw={vehicle.homenet_state}
                    stockType={vehicle.vehicle_type}
                  />
                </td>
                {showDetail && (
                  <td style={{ ...dataCellStyle(zebra), color: THEME.colors.neutral.light.textSecondary }}>
                    {isCounterpart ? (
                      vehicle.audit_sheet ?? "—"
                    ) : (
                      <>
                        {formatProblemDetail(vehicle.problem_detail, vehicle.problem_date) ?? "—"}
                        {/* A sale this fresh may be a booked deal that reverses
                            onto the same stock number, and nothing at flag time
                            tells the two apart — so the row still stands, it
                            just asks to be checked before the listing goes. */}
                        {isReversibleDeparture(vehicle.problem_date) && (
                          <div style={{ color: THEME.colors.semantic.warning, fontSize: 10.5 }}>
                            verify before removing
                          </div>
                        )}
                      </>
                    )}
                  </td>
                )}
                {showSiteLabel && (
                  <td style={{ ...dataCellStyle(zebra), color: THEME.colors.neutral.light.textSecondary }}>
                    {vehicle.site_label ?? "—"}
                  </td>
                )}
                {showSite && (
                  <td style={dataCellStyle(zebra)}>
                    {vehicle.website_url ? (
                      <a
                        href={vehicle.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        // The row click opens the drawer — the link must not.
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: THEME.colors.semantic.info, fontSize: 12, textDecoration: "none" }}
                      >
                        View →
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                )}
                {showTags && (
                  <td style={dataCellStyle(zebra)}>
                    {/* Tags are keyed by VIN — show them once, on the parent row. */}
                    {isCounterpart ? null : workedAt ? (
                      <span
                        style={{
                          display: "inline-block",
                          borderRadius: THEME.borderRadius.pill,
                          fontSize: 10, fontWeight: 700, letterSpacing: "0.6px",
                          padding: "2px 8px",
                          background: THEME.problemDisplay.severitySoft.warning,
                          color: THEME.problemDisplay.severityColors.warning,
                          whiteSpace: "nowrap",
                        }}
                      >
                        STILL FLAGGED · worked{" "}
                        {parseUtcTimestamp(workedAt).toLocaleDateString([], { month: "short", day: "numeric" })}
                      </span>
                    ) : showDaysOpen && vehicle.first_seen_at ? (
                      <span
                        style={{
                          display: "inline-block",
                          borderRadius: THEME.borderRadius.pill,
                          fontSize: 10, fontWeight: 600, letterSpacing: "0.3px",
                          padding: "2px 8px",
                          background: THEME.problemDisplay.severitySoft.cleanup,
                          color: THEME.problemDisplay.severityColors.cleanup,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDaysOpen(vehicle.first_seen_at)}
                      </span>
                    ) : isNew ? (
                      <span
                        style={{
                          display: "inline-block",
                          borderRadius: THEME.borderRadius.pill,
                          fontSize: 10, fontWeight: 700, letterSpacing: "0.6px",
                          padding: "2px 8px",
                          background: THEME.problemDisplay.severitySoft.info,
                          color: THEME.problemDisplay.severityColors.info,
                        }}
                      >
                        NEW
                      </span>
                    ) : null}
                  </td>
                )}
              </tr>
            )
            })
          })}
        </tbody>
      </table>
    </div>
  )
}
